import React, { useEffect, useState } from "react";
import { ShoppingBag, Loader2, ShieldCheck, Tag, ArrowRight, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useUserAuth } from "../context/UserAuthContext";

const CAT_COLOR = {
  "Entrenamiento Personal": "text-[#FFCC00] border-[#FFCC00]/30",
  "Plan de Dieta": "text-green-400 border-green-500/30",
  "Bono de Sesiones": "text-purple-400 border-purple-500/30",
  "Suplementación": "text-blue-400 border-blue-500/30",
  "Otro": "text-white/50 border-white/15",
};

export default function TiendaPage() {
  const { user } = useUserAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buyingId, setBuyingId] = useState(null);
  const [buyErr, setBuyErr] = useState("");
  const [filterCat, setFilterCat] = useState("");

  useEffect(() => {
    api.get("/productos").then(r => { setProducts(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const handleBuy = async (product) => {
    if (!user) { navigate("/login"); return; }
    setBuyingId(product.id); setBuyErr("");
    try {
      const { checkout_url } = await api.post("/payments/checkout", {
        product_id: product.id,
        origin_url: window.location.origin,
      }).then(r => r.data);
      window.location.href = checkout_url;
    } catch (e) {
      setBuyErr(formatApiError(e?.response?.data?.detail) || "Error al procesar el pago. Inténtalo de nuevo.");
      setBuyingId(null);
    }
  };

  const cats = [...new Set(products.map(p => p.categoria))].sort();
  const filtered = filterCat ? products.filter(p => p.categoria === filterCat) : products;

  return (
    <div className="min-h-screen bg-[#050505] text-white font-body" data-testid="tienda-page">
      {/* Header */}
      <div className="border-b border-white/8 px-6 py-10 text-center">
        <p className="text-[10px] tracking-[0.3em] uppercase font-black text-[#FFCC00] mb-3">XPRO FITNESS</p>
        <h1 className="font-display text-4xl md:text-5xl mb-3">Productos y Servicios</h1>
        <p className="text-white/40 text-sm max-w-md mx-auto">Entrena más inteligente. Elige el servicio que mejor se adapta a tu objetivo.</p>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-10">
        {/* Category filters */}
        {cats.length > 1 && (
          <div className="flex flex-wrap gap-2 mb-8">
            <button onClick={() => setFilterCat("")} data-testid="filter-all"
              className={`px-4 py-1.5 text-[10px] font-black tracking-[0.2em] uppercase border transition-colors ${!filterCat ? "bg-[#FFCC00] text-black border-[#FFCC00]" : "border-white/15 text-white/45 hover:border-white/40 hover:text-white"}`}>
              Todo
            </button>
            {cats.map(c => (
              <button key={c} onClick={() => setFilterCat(c)} data-testid={`filter-cat-${c}`}
                className={`px-4 py-1.5 text-[10px] font-black tracking-[0.2em] uppercase border transition-colors ${filterCat === c ? "bg-[#FFCC00] text-black border-[#FFCC00]" : "border-white/15 text-white/45 hover:border-white/40 hover:text-white"}`}>
                {c}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-24"><Loader2 size={28} className="animate-spin text-[#FFCC00]" /></div>
        ) : filtered.length === 0 ? (
          <div className="border border-white/8 p-16 text-center">
            <ShoppingBag size={40} className="mx-auto text-white/10 mb-4" />
            <p className="text-white/30 text-sm">No hay servicios disponibles en este momento.</p>
          </div>
        ) : (
          <>
            {buyErr && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 text-red-300 text-sm" data-testid="buy-error">
                {buyErr}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="tienda-grid">
              {filtered.map(p => (
                <ProductCard key={p.id} product={p} buying={buyingId === p.id} onBuy={() => handleBuy(p)} />
              ))}
            </div>
          </>
        )}

        {/* Trust badges */}
        <div className="mt-16 border-t border-white/5 pt-8 flex flex-wrap justify-center gap-8 text-white/25">
          <div className="flex items-center gap-2 text-xs">
            <ShieldCheck size={14} className="text-[#FFCC00]/60" />
            <span>Pago seguro con Stripe</span>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <Tag size={14} className="text-[#FFCC00]/60" />
            <span>Precios con IVA incluido</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProductCard({ product: p, buying, onBuy }) {
  const catColor = CAT_COLOR[p.categoria] || CAT_COLOR["Otro"];
  const agotado = p.stock_disponible !== null && p.stock_disponible !== undefined && p.stock_disponible <= 0;
  const hasDiscount = p.precio_original && p.precio_original > p.precio;
  const pctOff = hasDiscount ? Math.round((1 - p.precio / p.precio_original) * 100) : 0;

  return (
    <div className="group border border-white/8 hover:border-[#FFCC00]/30 bg-[#0a0a0a] flex flex-col transition-all duration-200" data-testid={`tienda-card-${p.id}`}>
      {p.imagen_url && (
        <div className="aspect-square overflow-hidden border-b border-white/5 relative">
          <img src={p.imagen_url} alt={p.nombre} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
          {hasDiscount && (
            <div className="absolute top-2.5 right-2.5 bg-red-500 text-white text-[10px] font-black px-2 py-0.5 uppercase tracking-wider">
              -{pctOff}%
            </div>
          )}
        </div>
      )}
      <div className="p-5 flex flex-col gap-3 flex-1">
        <span className={`self-start text-[9px] font-black tracking-[0.2em] uppercase px-2 py-0.5 border ${catColor}`}>
          {p.categoria}
        </span>
        <p className="text-base font-black text-white leading-tight">{p.nombre}</p>
        {p.descripcion && <p className="text-[12px] text-white/45 leading-relaxed line-clamp-3">{p.descripcion}</p>}

        {p.stock !== null && p.stock !== undefined && (
          <div className="flex items-center gap-1.5">
            <div className={`w-1.5 h-1.5 rounded-full ${agotado ? "bg-red-500" : "bg-green-500"}`} />
            <span className={`text-[10px] font-bold uppercase tracking-wider ${agotado ? "text-red-400" : "text-green-400"}`}>
              {agotado ? "Sin plazas" : `${p.stock_disponible} plazas disponibles`}
            </span>
          </div>
        )}

        <div className="flex items-center justify-between mt-auto pt-2">
          <div className="flex flex-col gap-0.5">
            {hasDiscount && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-white/30 line-through">{p.precio_original.toFixed(2)} €</span>
                {!p.imagen_url && (
                  <span className="text-[9px] font-black bg-red-500/15 text-red-400 border border-red-500/30 px-1.5 py-0.5 uppercase tracking-wider">
                    -{pctOff}%
                  </span>
                )}
              </div>
            )}
            <span className="font-display text-2xl text-[#FFCC00]">
              {p.precio.toFixed(2)}<span className="text-sm font-body ml-1">€</span>
            </span>
          </div>
          <button
            onClick={onBuy}
            disabled={buying || agotado}
            data-testid={`comprar-${p.id}`}
            className={`flex items-center gap-1.5 px-4 py-2.5 font-black text-[11px] tracking-[0.2em] uppercase transition-colors disabled:opacity-40 ${
              agotado ? "border border-white/10 text-white/25 cursor-not-allowed" :
              buying ? "bg-[#FFCC00]/30 text-black cursor-wait" :
              "bg-[#FFCC00] text-black hover:bg-white"
            }`}>
            {buying ? <Loader2 size={12} className="animate-spin" /> : <ArrowRight size={12} />}
            {agotado ? "Agotado" : buying ? "Redirigiendo..." : "Comprar"}
          </button>
        </div>
      </div>
    </div>
  );
}
